/**
 * ChecklistAssigneePicker — 149-cw-r135 (2026-08-31).
 *
 * Compact popover picker untuk memilih PIC per-item checklist.
 * Menampilkan avatar chip (initial + first name) atau "@ Pilih PIC" ketika kosong.
 * Popover: search box + user list dgn badge divisi. Support clear ("Tanpa PIC").
 *
 * Props:
 *   - value: user id yang sekarang terpilih (string | null)
 *   - valueName: nama yang sudah tersedia (fallback display, boleh null)
 *   - valueDivision: division badge color (boleh null)
 *   - users: array user objects [{id, name, division, sub_division}]
 *   - onChange: (id|null, userObj|null) => void
 *   - disabled: boolean
 *   - compact: kalau true, chip lebih kecil (untuk detail dialog)
 */
import { useMemo, useState } from "react";
import { UserCircle2, Search, X, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DIVISION_COLOR } from "@/lib/constants";

function _initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("");
}

export default function ChecklistAssigneePicker({ value, valueName, valueDivision, users = [], onChange, disabled = false, compact = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = (users || []).filter((u) => u && u.id);
    if (!s) return base.slice(0, 30);
    return base.filter((u) => `${u.name} ${u.division || ""} ${u.sub_division || ""}`.toLowerCase().includes(s)).slice(0, 30);
  }, [users, q]);

  const currentUser = useMemo(() => (users || []).find((u) => u.id === value) || null, [users, value]);
  const displayName = currentUser?.name || valueName || null;
  const displayDivision = currentUser?.division || valueDivision || null;

  const size = compact ? "h-6 text-[10px] px-1.5" : "h-7 text-[11px] px-2";

  return (
    <Popover open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid="checklist-assignee-btn"
          className={`inline-flex items-center gap-1 border rounded-none font-semibold uppercase tracking-wider shrink-0 transition-colors ${size} ${
            displayName
              ? "bg-[#0B1D3A] border-[#0B1D3A] text-white hover:bg-[#122a4d]"
              : "bg-white border-dashed border-slate-400 text-slate-500 hover:border-[#0B1D3A] hover:text-[#0B1D3A]"
          } disabled:opacity-60 disabled:cursor-not-allowed`}
          title={displayName ? `PIC: ${displayName}` : "Klik untuk pilih PIC item ini"}
        >
          {displayName ? (
            <>
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 bg-white/20 text-[8px] font-bold rounded-full shrink-0">
                {_initials(displayName)}
              </span>
              <span className="truncate max-w-[90px] normal-case tracking-normal">{displayName.split(" ")[0]}</span>
            </>
          ) : (
            <>
              <UserCircle2 size={compact ? 10 : 12} />
              <span className="normal-case tracking-normal">PIC</span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-64 p-0 rounded-none" data-testid="checklist-assignee-popover">
        <div className="border-b border-slate-200 p-2">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A]">PIC Sub-Tugas</div>
            {displayName && (
              <button
                type="button"
                data-testid="checklist-assignee-clear"
                onClick={() => { onChange?.(null, null); setOpen(false); }}
                className="text-[10px] uppercase tracking-wider font-semibold text-rose-600 hover:underline inline-flex items-center gap-0.5"
              >
                <X size={9} /> Clear
              </button>
            )}
          </div>
          <div className="relative">
            <Search size={11} className="absolute left-2 top-2 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              placeholder="Cari nama…"
              data-testid="checklist-assignee-search"
              className="w-full h-7 pl-7 pr-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none"
            />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-center text-[11px] italic text-slate-400">Tidak ada user cocok.</div>
          )}
          {filtered.map((u) => {
            const active = u.id === value;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => { onChange?.(u.id, u); setOpen(false); }}
                data-testid={`checklist-assignee-opt-${u.id}`}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] border-b border-slate-100 hover:bg-slate-50 ${active ? "bg-slate-50" : "bg-white"}`}
              >
                <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[9px] font-bold text-white bg-[#0B1D3A] shrink-0`}>
                  {_initials(u.name)}
                </span>
                {u.division && (
                  <span className={`px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[u.division] || "bg-slate-600 text-white"}`}>{u.division}</span>
                )}
                <span className="text-slate-800 truncate flex-1">{u.name}</span>
                {active && <Check size={11} className="text-emerald-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
